import { DncFieldMappingEnum } from './dnc-field-mapping-enums';
import type { DncFieldMappingValue } from './dnc-field-mapping-enums';
import { validateE164PhoneNumber } from './dnc-utils';
import { BaseFieldMapperUtil } from '@/shared/utils/base-field-mapper-util';
import type { BaseField } from '@/shared/utils/base-field-mapper-util';
import logger from '@/logger';

const log = logger.getChild('dnc-field-mapper-util');

export type DncField = BaseField<DncFieldMappingValue>;

const DNC_FIELDS_VALUES_SET = new Set<string>(
  Object.values(DncFieldMappingEnum),
);

export class DncFieldMapperUtil extends BaseFieldMapperUtil<DncFieldMappingValue> {
  readonly requiredFields: DncFieldMappingValue[] = [
    DncFieldMappingEnum.PhoneNumber,
    DncFieldMappingEnum.DoNotCall,
    DncFieldMappingEnum.DoNotText,
  ];

  readonly disabledDncFields: DncFieldMappingValue[] = [
    DncFieldMappingEnum.PhoneNumber,
    DncFieldMappingEnum.DoNotCall,
    DncFieldMappingEnum.DoNotText,
  ];

  readonly fieldsValuesSet = DNC_FIELDS_VALUES_SET;

  protected getLoggerName(): string {
    return 'dnc-field-mapper-util';
  }

  protected fillWithoutMap(fields: string[], examples: string[]): void {
    log.debug('Filling without map', { fields, examples });

    const phoneFieldIndex = this.detectPhoneFieldIndex(fields, examples);
    const doNotCallFieldIndex = this.detectDoNotCallFieldIndex(fields);
    const doNotTextFieldIndex = this.detectDoNotTextFieldIndex(fields);
    const usedIndices = new Set(
      [phoneFieldIndex, doNotCallFieldIndex, doNotTextFieldIndex].filter(
        (i) => i >= 0,
      ),
    );

    this.addRequiredFieldsIfDetected(fields, examples, {
      phoneFieldIndex,
      doNotCallFieldIndex,
      doNotTextFieldIndex,
    });

    this.addRemainingFields(fields, examples, usedIndices);
    this.ensureRequiredFieldsExist();
  }

  private detectPhoneFieldIndex(fields: string[], examples: string[]): number {
    for (let i = 0; i < fields.length; i++) {
      if (
        this.isPhoneFieldByName(fields[i]) ||
        this.isPhoneFieldByExample(examples[i])
      ) {
        return i;
      }
    }
    return -1;
  }

  private isPhoneFieldByName(fieldName: string): boolean {
    const lowerFieldName = fieldName.toLowerCase();
    return (
      lowerFieldName.includes('phone') ||
      lowerFieldName.includes('number') ||
      lowerFieldName.includes('tel') ||
      lowerFieldName.includes('mobile')
    );
  }

  private isPhoneFieldByExample(example: string): boolean {
    if (!example) return false;
    return (
      validateE164PhoneNumber(example) ||
      /^\d{10,15}$/.test(example.replace(/\D/g, ''))
    );
  }

  private detectDoNotCallFieldIndex(fields: string[]): number {
    for (let i = 0; i < fields.length; i++) {
      if (this.isDoNotCallFieldByName(fields[i])) {
        return i;
      }
    }
    return -1;
  }

  private isDoNotCallFieldByName(fieldName: string): boolean {
    const lowerFieldName = fieldName.toLowerCase();
    return (
      lowerFieldName.includes('do_not_call') ||
      lowerFieldName.includes('do not call') ||
      lowerFieldName.includes('dnc') ||
      lowerFieldName.includes('no_call') ||
      lowerFieldName.includes('no call') ||
      lowerFieldName.includes('call_block') ||
      lowerFieldName.includes('call block')
    );
  }

  private detectDoNotTextFieldIndex(fields: string[]): number {
    for (let i = 0; i < fields.length; i++) {
      if (this.isDoNotTextFieldByName(fields[i])) {
        return i;
      }
    }
    return -1;
  }

  private isDoNotTextFieldByName(fieldName: string): boolean {
    const lowerFieldName = fieldName.toLowerCase();
    return (
      lowerFieldName.includes('do_not_text') ||
      lowerFieldName.includes('do not text') ||
      lowerFieldName.includes('dnt') ||
      lowerFieldName.includes('no_text') ||
      lowerFieldName.includes('no text') ||
      lowerFieldName.includes('text_block') ||
      lowerFieldName.includes('text block') ||
      lowerFieldName.includes('sms_block') ||
      lowerFieldName.includes('sms block')
    );
  }

  private addRequiredFieldsIfDetected(
    fields: string[],
    examples: string[],
    indices: {
      phoneFieldIndex: number;
      doNotCallFieldIndex: number;
      doNotTextFieldIndex: number;
    },
  ) {
    const { phoneFieldIndex, doNotCallFieldIndex, doNotTextFieldIndex } =
      indices;

    if (phoneFieldIndex >= 0) {
      this.fields.value.push({
        original: fields[phoneFieldIndex],
        example: examples[phoneFieldIndex],
        dialpad: DncFieldMappingEnum.PhoneNumber,
      });
    }

    if (doNotCallFieldIndex >= 0) {
      this.fields.value.push({
        original: fields[doNotCallFieldIndex],
        example: examples[doNotCallFieldIndex],
        dialpad: DncFieldMappingEnum.DoNotCall,
      });
    }

    if (doNotTextFieldIndex >= 0) {
      this.fields.value.push({
        original: fields[doNotTextFieldIndex],
        example: examples[doNotTextFieldIndex],
        dialpad: DncFieldMappingEnum.DoNotText,
      });
    }
  }

  private addRemainingFields(
    fields: string[],
    examples: string[],
    usedIndices: Set<number>,
  ) {
    for (let i = 0; i < fields.length; i++) {
      if (usedIndices.has(i)) continue;

      this.fields.value.push({
        original: fields[i],
        example: examples[i],
        dialpad: null,
      });
    }
  }

  private ensureRequiredFieldsExist() {
    if (this.fields.value.length === 0) return;

    this.ensureFieldExists(DncFieldMappingEnum.PhoneNumber, 0);
    this.ensureFieldExists(DncFieldMappingEnum.DoNotCall);
    this.ensureFieldExists(DncFieldMappingEnum.DoNotText);
  }

  private ensureFieldExists(
    requiredField: DncFieldMappingValue,
    preferredIndex?: number,
  ) {
    const fieldIndex = this.fields.value.findIndex(
      (field) => field.dialpad === requiredField,
    );

    if (fieldIndex === -1) {
      const unmappedFieldIndex = this.fields.value.findIndex(
        (field) => field.dialpad === null,
      );

      if (unmappedFieldIndex >= 0) {
        this.fields.value[unmappedFieldIndex].dialpad = requiredField;
      } else {
        this.fields.value.push({
          original: `${requiredField}_placeholder`,
          example: '',
          dialpad: requiredField,
        });
      }
    } else if (preferredIndex !== undefined && fieldIndex !== preferredIndex) {
      const field = this.fields.value.splice(fieldIndex, 1)[0];
      this.fields.value.splice(preferredIndex, 0, field);
    }
  }

  protected performCustomValidation(
    index: number,
    newValue: DncFieldMappingValue | null | string,
  ): void {
    this.validatePhoneNumberChange(index, newValue);
  }

  protected getDoNotImportValue(): DncFieldMappingValue {
    return DncFieldMappingEnum.DoNotImport;
  }

  private validatePhoneNumberChange(
    index: number,
    newValue: DncFieldMappingValue | null | string,
  ): void {
    const isChangingAwayFromPhoneNumber =
      newValue !== DncFieldMappingEnum.PhoneNumber &&
      index < this.requiredFields.length &&
      this.fields.value[index].dialpad === DncFieldMappingEnum.PhoneNumber;

    if (isChangingAwayFromPhoneNumber && this.isOnlyPhoneNumberField(index)) {
      throw new Error('Cannot remove the only phone number mapping');
    }
  }

  private isOnlyPhoneNumberField(excludeIndex: number): boolean {
    const otherPhoneFields = this.fields.value.filter(
      (field, i) =>
        i !== excludeIndex && field.dialpad === DncFieldMappingEnum.PhoneNumber,
    );
    return otherPhoneFields.length === 0;
  }
}
